import { removeField } from "@lib/common";
import { rightsActions } from "@lib/rights";
import { proposalsTools } from "@features/proposals/proposals-tools";
import {
  ProgramProposalEntity,
  ProgramProposalOutputView,
} from "@features/events-program/types/program-proposal";
import {
  ProgramProposalEntityRightsTypes,
  ProposalAttachmentEntityRightsTypes,
  ProposalCommentEntityRightsTypes,
} from "@features/events-program/types/rights";
import { ProposalEntityRights } from "@features/proposals/types/rights";
import {
  ProposalVoteDomainView,
  ProposalVoteEntity,
  ProposalVoteOutputView,
  ProposalVoteResolution,
} from "./types/proposal-vote";
import {
  ProposalAssignmentCandidateEntity,
  ProposalAssignmentCandidateOutputView,
  ProposalAssignmentEntity,
  ProposalAssignmentOutputView,
} from "./types/proposal-assignment";
import {
  ProposalAssignmentAggregateEntity,
  ProposalAssignmentAggregateOutputView,
  ProposalPreview,
  ProposalPreviewEntity,
} from "./types/proposal-assignment-aggregate";
import {
  ProposalCommentPreviewEntity,
  ProposalCommentPreviewOutputView,
  ProposalCommentReplyEntity,
  ProposalCommentReplyOutputView,
  ProposalCommentRootEntity,
  ProposalCommentRootOutputView,
  ProposalCommentThreadEntity,
  ProposalCommentThreadOutputView,
} from "./types/proposal-comment";
import {
  ProposalAttachmentEntity,
  ProposalAttachmentOutputView,
} from "./types/proposal-attachment";
import { teamsTools } from "@features/teams/teams-tools";

const normalizeVoteResolution = (voteResolution: string): ProposalVoteResolution => {
  if (voteResolution === "ACCEPT" || voteResolution === "REJECT" || voteResolution === "ABSTAIN") {
    return voteResolution;
  }

  return "ABSTAIN";
};

const convertVoteToDomainEntity = (vote: ProposalVoteOutputView): ProposalVoteEntity => {
  const normalizedVote: ProposalVoteDomainView = {
    ...vote,
    voteResolution: normalizeVoteResolution(vote.voteResolution),
  };

  return rightsActions.parseActions<ProposalVoteDomainView, never, ProposalVoteEntity>(
    normalizedVote,
    (actions, fns) =>
      fns.canEntityRightsForAnyResource(actions, ["proposal-vote", "proposal/vote"]),
  );
};

const convertAssignmentToDomainEntity = (
  assignment: ProposalAssignmentOutputView,
): ProposalAssignmentEntity =>
  rightsActions.parseActions<ProposalAssignmentOutputView, never, ProposalAssignmentEntity>(
    assignment,
    (actions, fns) =>
      fns.canEntityRightsForAnyResource(actions, ["proposal-assignment", "proposal/assignment"]),
  );

const convertAssignmentCandidateToDomainEntity = (
  candidate: ProposalAssignmentCandidateOutputView,
): ProposalAssignmentCandidateEntity => ({
  ...candidate,
  teamMember: candidate.teamMember && teamsTools.addRightsToTeamMember(candidate.teamMember),
});

type ProposalPreviewEntityWithOutputVote = Omit<ProposalPreviewEntity, "vote"> & {
  readonly vote?: ProposalVoteOutputView;
};

const convertProposalPreviewToDomainEntity = (proposal: ProposalPreview): ProposalPreviewEntity => {
  const preview = rightsActions.parseActions<
    ProposalPreview,
    ProgramProposalEntityRightsTypes,
    ProposalPreviewEntityWithOutputVote
  >(proposal, (actions, fns) => ({
    canRead: fns.canAny(actions, ["proposal", "program/proposal"], "read"),
    canPatch: fns.canAny(actions, ["proposal", "program/proposal"], "patch"),
    canDelete: fns.canAny(actions, ["proposal", "program/proposal"], "delete"),
    ...proposalsTools.addAdditionalRightsToProposal({ fns, actions }),
  }));

  return {
    ...preview,
    vote: preview.vote && convertVoteToDomainEntity(preview.vote),
  };
};

const convertAssignmentAggregateToDomainEntity = (
  aggregate: ProposalAssignmentAggregateOutputView,
): ProposalAssignmentAggregateEntity => {
  const aggregateWithoutActions = removeField(aggregate, "actions") as Omit<
    ProposalAssignmentAggregateOutputView,
    "actions"
  >;

  return {
    ...aggregateWithoutActions,
    assignment:
      aggregateWithoutActions.assignment &&
      convertAssignmentToDomainEntity(aggregateWithoutActions.assignment),
    proposal:
      aggregateWithoutActions.proposal &&
      convertProposalPreviewToDomainEntity(aggregateWithoutActions.proposal),
  };
};

const convertAttachmentToDomainEntity = (
  attachment: ProposalAttachmentOutputView,
): ProposalAttachmentEntity =>
  rightsActions.parseActions<
    ProposalAttachmentOutputView,
    ProposalAttachmentEntityRightsTypes,
    ProposalAttachmentEntity
  >(attachment, (actions, fns) => ({
    ...fns.canEntityRightsForAnyResource(actions, ["proposal-attachment", "proposal/attachment"]),
    canGetContent: fns.canAny(actions, ["proposal-attachment", "proposal/attachment"], "content"),
    canDelete: fns.canAny(actions, ["proposal-attachment", "proposal/attachment"], "delete"),
  }));

const convertCommentPreviewToDomainEntity = (
  commentPreview: ProposalCommentPreviewOutputView,
): ProposalCommentPreviewEntity =>
  removeField({ ...commentPreview }, "actions") as ProposalCommentPreviewEntity;

const proposalCommentActionResources: Array<"proposal-comment" | "proposal/comment"> = [
  "proposal-comment",
  "proposal/comment",
];

const addRightsToProposalComment = <
  TOutput extends ProposalCommentReplyOutputView | ProposalCommentRootOutputView,
  TEntity extends ProposalCommentReplyEntity | ProposalCommentRootEntity,
>(
  comment: TOutput,
): TEntity =>
  rightsActions.parseActions<TOutput, ProposalCommentEntityRightsTypes, TEntity>(
    comment,
    (actions, fns) => ({
      ...fns.canEntityRightsForAnyResource(actions, proposalCommentActionResources),
      canReply: fns.canAny(actions, proposalCommentActionResources, "reply"),
      canResolve: fns.canAny(actions, proposalCommentActionResources, "resolve"),
    }),
  );

const convertCommentReplyToDomainEntity = (
  comment: ProposalCommentReplyOutputView,
): ProposalCommentReplyEntity =>
  addRightsToProposalComment<ProposalCommentReplyOutputView, ProposalCommentReplyEntity>(comment);

const convertCommentRootToDomainEntity = (
  comment: ProposalCommentRootOutputView,
): ProposalCommentRootEntity =>
  addRightsToProposalComment<ProposalCommentRootOutputView, ProposalCommentRootEntity>(comment);

const convertCommentThreadToDomainEntity = (
  thread: ProposalCommentThreadOutputView,
): ProposalCommentThreadEntity => ({
  ...thread,
  root: thread.root ? convertCommentRootToDomainEntity(thread.root) : undefined,
  replies: thread.replies?.map(convertCommentReplyToDomainEntity),
});

const addRightsToProgramProposal = (proposal: ProgramProposalOutputView): ProgramProposalEntity =>
  rightsActions.parseActions<
    ProgramProposalOutputView,
    ProgramProposalEntityRightsTypes,
    ProgramProposalEntity
  >(
    proposal,
    (actions, fns) =>
      ({
        canRead: fns.canAny(actions, ["proposal", "program/proposal"], "read"),
        canPatch: fns.canAny(actions, ["proposal", "program/proposal"], "patch"),
        canDelete: fns.canAny(actions, ["proposal", "program/proposal"], "delete"),
        ...proposalsTools.addAdditionalRightsToProposal({ fns, actions }),
      }) as ProposalEntityRights,
  );

export const programProposalsTools = {
  convertVoteToDomainEntity,
  convertAssignmentToDomainEntity,
  convertAssignmentAggregateToDomainEntity,
  convertAssignmentCandidateToDomainEntity,
  convertAttachmentToDomainEntity,
  convertCommentReplyToDomainEntity,
  convertCommentRootToDomainEntity,
  convertCommentThreadToDomainEntity,
  convertToDomainEntity: (proposal: ProgramProposalOutputView): ProgramProposalEntity => ({
    ...addRightsToProgramProposal(proposal),
    activity:
      proposal.activity && proposalsTools.activityTools.convertToDomainEntity(proposal.activity),
    members: proposal.members?.map(proposalsTools.activityMemberTools.convertToDomainEntity),
    votes: proposal.votes?.map(convertVoteToDomainEntity),
    assignments: proposal.assignments?.map(convertAssignmentToDomainEntity),
    attachments: proposal.attachments?.map(convertAttachmentToDomainEntity),
    lastCommentPreview:
      proposal.lastCommentPreview &&
      convertCommentPreviewToDomainEntity(proposal.lastCommentPreview),
    uiLinks: proposalsTools.getUiLinks(proposal.id),
  }),
};
